import type { QdsConfigField, QdsPropertyFieldPluginElement } from '../../types/blocks.js';
import { adoptPrimeIconsStyles, getPrimeIconsCssUrl, loadBaseCSS } from '../shared/button-utils.js';

type IconLibrary = 'prime' | 'lucide';

interface IconOption {
  name: string;
  library: IconLibrary;
}

type LucideRuntime = {
  createElement?: (icon: unknown) => SVGElement;
  [name: string]: unknown;
};

const PRIME_FALLBACK_ICONS = [
  'pi-align-center', 'pi-arrow-left', 'pi-arrow-right', 'pi-bell', 'pi-bookmark', 'pi-briefcase',
  'pi-building', 'pi-calendar', 'pi-chart-bar', 'pi-chart-line', 'pi-check', 'pi-cloud', 'pi-code',
  'pi-cog', 'pi-compass', 'pi-database', 'pi-download', 'pi-envelope', 'pi-external-link', 'pi-file',
  'pi-folder', 'pi-globe', 'pi-heart', 'pi-home', 'pi-image', 'pi-info-circle', 'pi-lightbulb',
  'pi-link', 'pi-lock', 'pi-map-marker', 'pi-pencil', 'pi-plus', 'pi-search', 'pi-send', 'pi-star',
  'pi-tag', 'pi-users'
];

const LUCIDE_FALLBACK_ICONS = [
  'activity', 'alarm-clock', 'arrow-left', 'arrow-right', 'bell', 'book-open', 'bookmark', 'briefcase',
  'building-2', 'calendar', 'chart-bar', 'chart-line', 'check', 'circle-help', 'cloud', 'code-2', 'cog',
  'database', 'download', 'file', 'folder', 'globe-2', 'heart', 'home', 'image', 'lightbulb', 'link',
  'map-pin', 'pencil', 'search', 'send', 'star', 'tag', 'users'
];

let primeIconsPromise: Promise<string[]> | null = null;
let lucidePromise: Promise<LucideRuntime | null> | null = null;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function toKebabCase(value: string): string {
  return value.replace(/([A-Z])/g, (match, letter, index) => index === 0 ? letter.toLowerCase() : `-${letter.toLowerCase()}`);
}

function getLucideRuntime(): LucideRuntime | null {
  const candidate = (globalThis as typeof globalThis & { lucide?: unknown }).lucide;
  return candidate && typeof candidate === 'object' ? candidate as LucideRuntime : null;
}

function loadPrimeIcons(): Promise<string[]> {
  if (!primeIconsPromise) {
    primeIconsPromise = fetch(getPrimeIconsCssUrl())
      .then((response) => response.ok ? response.text() : Promise.reject(new Error('Unable to load PrimeIcons')))
      .then((css) => [...css.matchAll(/\.(pi-[\w-]+):before/g)].map((match) => match[1]).sort())
      .catch(() => PRIME_FALLBACK_ICONS);
  }
  return primeIconsPromise;
}

function loadLucide(): Promise<LucideRuntime | null> {
  const existing = getLucideRuntime();
  if (existing) return Promise.resolve(existing);
  if (!lucidePromise) {
    lucidePromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.min.js';
      script.async = true;
      script.onload = () => resolve(getLucideRuntime());
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    });
  }
  return lucidePromise;
}

function getLucideOptions(runtime: LucideRuntime | null): string[] {
  if (!runtime) return LUCIDE_FALLBACK_ICONS;
  const skipped = new Set(['createIcons', 'createElement', 'replaceAll', 'icons', 'default', 'toSvg']);
  return Object.keys(runtime)
    .filter((name) => !skipped.has(name) && /^[A-Z]/.test(name))
    .map(toKebabCase)
    .sort();
}

function renderLucideIcon(name: string, className: string): string | null {
  const runtime = getLucideRuntime();
  if (!runtime?.createElement) return null;
  const entry = Object.entries(runtime).find(([key]) => toKebabCase(key) === name);
  if (!entry) return null;
  const svg = runtime.createElement(entry[1]);
  svg.setAttribute('class', className);
  svg.setAttribute('aria-hidden', 'true');
  return svg.outerHTML;
}

/**
 * @element qds-icon-picker
 * @property {string} value - A `prime:pi-*` or `lucide:*` icon value.
 * @fires value-change - Emits the selected icon value.
 */
export class QdsIconPicker extends HTMLElement implements QdsPropertyFieldPluginElement {
  private _property?: string;
  private _value: unknown = '';
  private _metadata?: QdsConfigField;
  private _context?: unknown;
  private _target?: Record<string, unknown>;
  private _readonly = false;
  private _open = false;
  private _activeLibrary: IconLibrary = 'prime';
  private _query = '';
  private _primeIcons = PRIME_FALLBACK_ICONS;
  private _lucideIcons = LUCIDE_FALLBACK_ICONS;
  private readonly _handleClickBound: EventListener;
  private readonly _handleInputBound: EventListener;
  private readonly _handleKeydownBound: EventListener;

  static get observedAttributes(): string[] {
    return ['readonly'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
    this._handleClickBound = (event) => this._handleClick(event as MouseEvent);
    this._handleInputBound = (event) => this._handleInput(event);
    this._handleKeydownBound = (event) => this._handleKeydown(event as KeyboardEvent);
    this.render();
  }

  connectedCallback(): void {
    this.shadowRoot?.addEventListener('click', this._handleClickBound);
    this.shadowRoot?.addEventListener('input', this._handleInputBound);
    this.shadowRoot?.addEventListener('keydown', this._handleKeydownBound);
    if (this.shadowRoot) void adoptPrimeIconsStyles(this.shadowRoot);
  }

  disconnectedCallback(): void {
    this.shadowRoot?.removeEventListener('click', this._handleClickBound);
    this.shadowRoot?.removeEventListener('input', this._handleInputBound);
    this.shadowRoot?.removeEventListener('keydown', this._handleKeydownBound);
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    if (name === 'readonly') {
      this._readonly = newValue !== null;
      this.render();
    }
  }

  set property(value: string | undefined) { this._property = value; }
  get property(): string | undefined { return this._property; }
  set value(value: unknown) {
    this._value = typeof value === 'string' ? value : '';
    this._activeLibrary = this._getLibrary(this._getValue()) || this._activeLibrary;
    this.render();
  }
  get value(): unknown { return this._value; }
  set metadata(value: QdsConfigField | undefined) { this._metadata = value; }
  get metadata(): QdsConfigField | undefined { return this._metadata; }
  set context(value: unknown) { this._context = value; }
  get context(): unknown { return this._context; }
  set target(value: Record<string, unknown> | undefined) { this._target = value; }
  get target(): Record<string, unknown> | undefined { return this._target; }
  set readonly(value: boolean) { this._readonly = Boolean(value); this.render(); }
  get readonly(): boolean { return this._readonly; }

  open(): void { this._openPicker(); }

  private _getValue(): string { return typeof this._value === 'string' ? this._value.trim() : ''; }
  private _getLibrary(value: string): IconLibrary | null {
    if (value.startsWith('prime:')) return 'prime';
    if (value.startsWith('lucide:')) return 'lucide';
    return null;
  }
  private _getIconName(value: string): string { return value.replace(/^(prime|lucide):/, ''); }
  private _getOptions(): IconOption[] {
    const icons = this._activeLibrary === 'prime' ? this._primeIcons : this._lucideIcons;
    const query = this._query.trim().toLowerCase();
    return icons.filter((name) => !query || name.toLowerCase().includes(query))
      .map((name) => ({ name, library: this._activeLibrary }));
  }
  private _openPicker(): void {
    if (this._readonly) return;
    this._activeLibrary = this._getLibrary(this._getValue()) || this._activeLibrary;
    this._query = '';
    this._open = true;
    this.render();
    void this._loadActiveLibrary();
  }
  private _closePicker(): void { this._open = false; this._query = ''; this.render(); }
  private async _loadActiveLibrary(): Promise<void> {
    if (this._activeLibrary === 'prime') {
      this._primeIcons = await loadPrimeIcons();
    } else {
      this._lucideIcons = getLucideOptions(await loadLucide());
    }
    if (this._open) this.render();
  }
  private _select(icon: IconOption): void {
    this._value = `${icon.library}:${icon.name}`;
    this._open = false;
    this._query = '';
    this.render();
    this.dispatchEvent(new CustomEvent('value-change', { detail: { value: this._value }, bubbles: true, composed: true }));
  }
  private _clear(): void {
    this._value = '';
    this._open = false;
    this.render();
    this.dispatchEvent(new CustomEvent('value-change', { detail: { value: '' }, bubbles: true, composed: true }));
  }
  private _handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const actionElement = target?.closest<HTMLElement>('[data-action]');
    const action = actionElement?.dataset.action;
    if (action === 'open') { this._openPicker(); return; }
    if (action === 'close') { this._closePicker(); return; }
    if (action === 'backdrop' && target === actionElement) { this._closePicker(); return; }
    if (action === 'clear') { this._clear(); return; }
    const tab = target?.closest<HTMLButtonElement>('.icon-picker__tab[data-library]')?.dataset.library as IconLibrary | undefined;
    if (tab === 'prime' || tab === 'lucide') {
      this._activeLibrary = tab;
      this._query = '';
      this.render();
      void this._loadActiveLibrary();
      return;
    }
    const option = target?.closest<HTMLButtonElement>('[data-icon]');
    if (option?.dataset.icon && option.dataset.library) this._select({ name: option.dataset.icon, library: option.dataset.library as IconLibrary });
  }
  private _handleInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (input?.dataset.role !== 'search') return;
    this._query = input.value;
    this.render();
    queueMicrotask(() => {
      const next = this.shadowRoot?.querySelector<HTMLInputElement>('[data-role="search"]');
      next?.focus();
      next?.setSelectionRange(this._query.length, this._query.length);
    });
  }
  private _handleKeydown(event: KeyboardEvent): void {
    if (this._open && event.key === 'Escape') { event.preventDefault(); this._closePicker(); }
  }
  private _renderIcon(value: string, className = 'icon-picker__preview-icon'): string {
    const library = this._getLibrary(value);
    const name = this._getIconName(value);
    if (library === 'prime') return `<i class="pi ${escapeHtml(name)} ${className}" aria-hidden="true"></i>`;
    if (library === 'lucide') return renderLucideIcon(name, className)
      || `<span class="${className} icon-picker__lucide-fallback" aria-hidden="true">◇</span>`;
    return `<span class="${className} icon-picker__empty" aria-hidden="true">+</span>`;
  }
  private _renderGrid(): string {
    const selected = this._getValue();
    const options = this._getOptions();
    if (!options.length) return '<p class="icon-picker__empty-state">No icons match your search.</p>';
    return `<div class="icon-picker__grid" role="listbox" aria-label="${this._activeLibrary === 'prime' ? 'PrimeIcons' : 'Lucide'} icons">${options.map((icon) => {
      const value = `${icon.library}:${icon.name}`;
      const selectedAttribute = selected === value ? 'aria-selected="true" data-selected="true"' : 'aria-selected="false"';
      return `<button type="button" class="icon-picker__option" data-icon="${escapeHtml(icon.name)}" data-library="${icon.library}" ${selectedAttribute} role="option" title="${escapeHtml(icon.name)}">${this._renderIcon(value, 'icon-picker__option-icon')}<span>${escapeHtml(icon.name.replace(/^pi-/, ''))}</span></button>`;
    }).join('')}</div>`;
  }
  render(): void {
    if (!this.shadowRoot) return;
    const value = this._getValue();
    const name = this._getIconName(value);
    const label = name ? name.replace(/^pi-/, '') : 'Choose icon';
    const disabled = this._readonly ? 'disabled' : '';
    this.shadowRoot.innerHTML = `
      <style>
        :host { display:block; min-width:0; font-family:var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif)); color:var(--theme-text-primary,#333); }
        *,*::before,*::after { box-sizing:border-box; }
        .icon-picker__trigger { width:100%; min-height:40px; padding:6px 10px; display:flex; align-items:center; gap:8px; border:1px solid var(--theme-border-primary,#e0e0e0); border-radius:var(--theme-radius-sm,4px); background:var(--theme-background-primary,#fff); color:inherit; cursor:pointer; font:inherit; text-align:left; }
        .icon-picker__trigger:hover:not(:disabled) { background:var(--theme-background-secondary,#f8f8f8); border-color:var(--theme-border-strong,#afb8c9); }
        .icon-picker__trigger:focus-visible,.icon-picker__option:focus-visible,.icon-picker__tab:focus-visible,.icon-picker__clear:focus-visible { outline:2px solid var(--theme-accent-primary,#445e89); outline-offset:2px; }
        .icon-picker__trigger:disabled { cursor:default; opacity:.65; }
        .icon-picker__preview-icon { width:20px; min-width:20px; height:20px; display:inline-grid; place-items:center; font-size:18px; }
        .icon-picker__label { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .icon-picker__backdrop { position:fixed; inset:0; z-index:1000; display:${this._open ? 'flex' : 'none'}; align-items:center; justify-content:center; padding:20px; background:var(--theme-background-overlay-subtle,rgba(255,255,255,.88)); }
        .icon-picker__dialog { display:grid; grid-template-rows:auto auto auto minmax(0,1fr); gap:12px; width:min(820px,100%); height:min(720px,82vh); padding:16px; overflow:hidden; border:1px solid var(--theme-border-primary,#e0e0e0); border-radius:var(--theme-radius-md,8px); background:var(--theme-background-primary,#fff); box-shadow:var(--theme-shadow-md,0 8px 24px rgba(0,0,0,.14)); }
        .icon-picker__header,.icon-picker__tabs { display:flex; align-items:center; gap:8px; }
        .icon-picker__header { justify-content:space-between; }
        .icon-picker__title { margin:0; font-size:var(--theme-font-size-md,16px); font-weight:var(--theme-font-weight-semibold,600); }
        .icon-picker__close,.icon-picker__clear { min-height:36px; border:1px solid var(--theme-border-primary,#e0e0e0); border-radius:4px; background:var(--theme-background-primary,#fff); color:inherit; cursor:pointer; font:inherit; padding:0 10px; }
        .icon-picker__tabs { border-bottom:1px solid var(--theme-border-primary,#e0e0e0); }
        .icon-picker__tab { border:0; border-bottom:2px solid transparent; background:transparent; color:var(--theme-text-secondary,#555); padding:8px 10px; cursor:pointer; font:inherit; font-weight:var(--theme-font-weight-medium,500); }
        .icon-picker__tab[data-active="true"] { color:var(--theme-accent-primary,#445e89); border-bottom-color:var(--theme-accent-primary,#445e89); }
        .icon-picker__search { width:100%; min-height:40px; padding:8px 12px; border:1px solid var(--theme-border-primary,#e0e0e0); border-radius:4px; background:var(--theme-background-primary,#fff); color:inherit; font:inherit; }
        .icon-picker__grid { min-height:0; overflow:auto; display:grid; grid-template-columns:repeat(auto-fill,minmax(112px,1fr)); gap:8px; padding:2px; }
        .icon-picker__option { min-height:76px; display:grid; grid-template-rows:28px auto; justify-items:center; align-content:center; gap:5px; border:1px solid var(--theme-border-primary,#e0e0e0); border-radius:4px; background:var(--theme-background-primary,#fff); color:inherit; cursor:pointer; font:inherit; font-size:12px; overflow:hidden; }
        .icon-picker__option:hover,.icon-picker__option[data-selected="true"] { border-color:var(--theme-accent-primary,#445e89); background:var(--theme-background-hover,rgba(0,0,0,.05)); }
        .icon-picker__option-icon { font-size:22px; width:24px; height:24px; display:grid; place-items:center; }
        .icon-picker__option span:last-child { max-width:100%; padding:0 5px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .icon-picker__lucide-fallback { color:var(--theme-text-secondary,#666); font-size:25px; line-height:1; }
        .icon-picker__empty-state { margin:auto; color:var(--theme-text-secondary,#555); }
        @media (max-width:600px) { .icon-picker__backdrop { padding:8px; } .icon-picker__dialog { height:calc(100vh - 16px); } .icon-picker__grid { grid-template-columns:repeat(auto-fill,minmax(88px,1fr)); } }
      </style>
      <button type="button" class="icon-picker__trigger" data-action="open" aria-haspopup="dialog" aria-expanded="${this._open}" ${disabled}>${this._renderIcon(value)}<span class="icon-picker__label">${escapeHtml(label)}</span></button>
      <div class="icon-picker__backdrop" data-action="backdrop">
        <section class="icon-picker__dialog" role="dialog" aria-modal="true" aria-label="Choose an icon">
          <div class="icon-picker__header"><h2 class="icon-picker__title">Choose an icon</h2><div><button type="button" class="icon-picker__clear" data-action="clear">Clear</button><button type="button" class="icon-picker__close" data-action="close" aria-label="Close icon picker">Close</button></div></div>
          <div class="icon-picker__tabs" role="tablist"><button type="button" class="icon-picker__tab" data-library="prime" data-active="${this._activeLibrary === 'prime'}" role="tab" aria-selected="${this._activeLibrary === 'prime'}">PrimeIcons</button><button type="button" class="icon-picker__tab" data-library="lucide" data-active="${this._activeLibrary === 'lucide'}" role="tab" aria-selected="${this._activeLibrary === 'lucide'}">Lucide</button></div>
          <input class="icon-picker__search" data-role="search" type="search" value="${escapeHtml(this._query)}" placeholder="Search ${this._activeLibrary === 'prime' ? 'PrimeIcons' : 'Lucide'}" aria-label="Search icons">
          ${this._renderGrid()}
        </section>
      </div>`;
    if (this.shadowRoot) void adoptPrimeIconsStyles(this.shadowRoot);
  }
}

if (!customElements.get('qds-icon-picker')) customElements.define('qds-icon-picker', QdsIconPicker);

export default QdsIconPicker;
